import React, { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Alert } from 'react-bootstrap'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

const EMPTY_FORM = {
  name: '',
  description: '',
  location: '',
  day_of_week: 'Lundi',
  start_time: '',
  end_time: '',
}

function Courses() {
  const [courseTypes, setCourseTypes] = useState([])

  const [showModal, setShowModal] = useState(false)
  const [editingCourse, setEditingCourse] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [formLoading, setFormLoading] = useState(false)

  const [archiveTarget, setArchiveTarget] = useState(null)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [archiveLoading, setArchiveLoading] = useState(false)

  useEffect(() => {
    fetchCourseTypes()
  }, [])

  const fetchCourseTypes = async () => {
    const { data, error } = await supabase
      .from('course_types')
      .select('*')
      .eq('is_active', true)
      .order('day_of_week', { ascending: true })
      .order('start_time', { ascending: true })
    if (error) { console.error(error); return }
    setCourseTypes(data || [])
  }

  const openCreateModal = () => {
    setEditingCourse(null)
    setFormData(EMPTY_FORM)
    setFormError('')
    setShowModal(true)
  }

  const openEditModal = (course) => {
    setEditingCourse(course)
    setFormData({
      name: course.name || '',
      description: course.description || '',
      location: course.location || '',
      day_of_week: course.day_of_week || 'Lundi',
      start_time: course.start_time ? String(course.start_time).substring(0, 5) : '',
      end_time: course.end_time ? String(course.end_time).substring(0, 5) : '',
    })
    setFormError('')
    setShowModal(true)
  }

  const handleFormChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    if (!formData.name.trim()) { setFormError('Le nom est requis.'); return }
    if (!formData.start_time || !formData.end_time) { setFormError("L'heure de début et de fin sont requises."); return }

    setFormLoading(true)
    let error

    if (editingCourse) {
      ({ error } = await supabase.from('course_types').update(formData).eq('id', editingCourse.id))
    } else {
      ({ error } = await supabase.from('course_types').insert([formData]))
    }

    setFormLoading(false)
    if (error) { setFormError('Erreur: ' + error.message); return }
    setShowModal(false)
    fetchCourseTypes()
  }

  const askArchiveCourse = (course) => {
    setArchiveTarget({ id: course.id, name: course.name })
    setShowArchiveModal(true)
  }

  const confirmArchive = async () => {
    if (!archiveTarget) return
    setArchiveLoading(true)
    // Archivage : le cours disparaît du programme mais l'historique des présences est conservé
    const { error } = await supabase.from('course_types').update({ is_active: false }).eq('id', archiveTarget.id)
    setArchiveLoading(false)
    if (error) { alert('Erreur archivage: ' + error.message); return }
    setShowArchiveModal(false)
    setArchiveTarget(null)
    fetchCourseTypes()
  }

  const generateInviteLink = async () => {
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 7)
    const { error } = await supabase
      .from('invite_tokens')
      .insert([{ token, expires_at: expiresAt.toISOString() }])
    if (error) { alert("Erreur génération lien: " + error.message); return }
    const inviteUrl = `${window.location.origin}/register?invite=${token}`
    await navigator.clipboard.writeText(inviteUrl)
    alert("Lien d'invitation copié dans le presse-papiers!")
  }

  const getDayColor = (day) => {
    const colors = {
      'Lundi': 'primary', 'Mardi': 'success', 'Mercredi': 'warning',
      'Jeudi': 'info', 'Vendredi': 'secondary', 'Samedi': 'danger', 'Dimanche': 'dark',
    }
    return colors[day] || 'light'
  }

  const formatTime = (t) => t ? String(t).substring(0, 5) : ''

  const groupedCourses = DAYS.reduce((acc, day) => {
    acc[day] = courseTypes.filter(c => c.day_of_week === day)
    return acc
  }, {})

  return (
    <ProtectedRoute>
      <Layout user={{}}>

        {/* En-tête page */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gestion des Cours</h2>
          <Button variant="success" onClick={generateInviteLink} size="lg">
            Générer Lien d'Inscription
          </Button>
        </div>

        {/* ── Programme hebdomadaire ── */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <h4 className="mb-0">📅 Programme Hebdomadaire Street Sport Luxembourg</h4>
            <Button variant="outline-light" size="sm" onClick={openCreateModal}>
              + Ajouter un cours
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <Table className="mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead className="bg-light">
                <tr>
                  {DAYS.map(day => (
                    <th key={day} className="text-center" style={{ width: `${100 / 7}%`, padding: '6px 4px' }}>
                      <Badge bg={getDayColor(day)} style={{ fontSize: '0.7rem', padding: '4px 6px', display: 'block' }}>
                        {day}
                      </Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {DAYS.map(day => (
                    <td key={day} style={{ verticalAlign: 'top', padding: '4px' }}>
                      {groupedCourses[day].map(course => (
                        <Card key={course.id} className="mb-1" style={{ border: '1px solid #dee2e6' }}>
                          <Card.Body className="p-2">
                            <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '2px', lineHeight: '1.2' }}>
                              {course.name}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6c757d', lineHeight: '1.3' }}>
                              ⏰ {formatTime(course.start_time)} - {formatTime(course.end_time)}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6c757d', lineHeight: '1.3' }}>
                              📍 {course.location}
                            </div>
                            {course.description && (
                              <div style={{ fontSize: '0.65rem', color: '#6c757d', lineHeight: '1.3' }}>
                                📝 {course.description}
                              </div>
                            )}
                            <div className="d-flex gap-1 mt-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openEditModal(course)}
                                title="Modifier"
                                style={{ fontSize: '0.65rem', padding: '1px 6px', lineHeight: '1.4' }}
                              >
                                ✏️
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => askArchiveCourse(course)}
                                title="Archiver"
                                style={{ fontSize: '0.65rem', padding: '1px 6px', lineHeight: '1.4' }}
                              >
                                📦
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                      {groupedCourses[day].length === 0 && (
                        <div className="text-center text-muted py-3" style={{ fontSize: '0.7rem' }}>
                          Aucun cours
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* ── Modale création / édition ── */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingCourse ? 'Modifier le cours' : 'Ajouter un cours'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form onSubmit={handleFormSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nom *</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleFormChange} placeholder="ex: Football" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Jour *</Form.Label>
                <Form.Select name="day_of_week" value={formData.day_of_week} onChange={handleFormChange}>
                  {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Form.Group>
              <div className="row">
                <Form.Group className="mb-3 col-6">
                  <Form.Label>Heure début *</Form.Label>
                  <Form.Control type="time" name="start_time" value={formData.start_time} onChange={handleFormChange} />
                </Form.Group>
                <Form.Group className="mb-3 col-6">
                  <Form.Label>Heure fin *</Form.Label>
                  <Form.Control type="time" name="end_time" value={formData.end_time} onChange={handleFormChange} />
                </Form.Group>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Lieu</Form.Label>
                <Form.Control name="location" value={formData.location} onChange={handleFormChange} placeholder="ex: Parc Merl" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleFormChange} />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
                <Button variant="primary" type="submit" disabled={formLoading}>
                  {formLoading ? 'Enregistrement...' : editingCourse ? 'Modifier' : 'Ajouter'}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ── Modale confirmation suppression ── */}
        <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)} centered size="sm">
          <Modal.Header closeButton>
            <Modal.Title>Archiver le cours</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Retirer <strong>{archiveTarget?.name}</strong> du programme ? L'historique des présences est conservé.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>Annuler</Button>
            <Button variant="warning" onClick={confirmArchive} disabled={archiveLoading}>
              {archiveLoading ? 'Archivage...' : 'Archiver'}
            </Button>
          </Modal.Footer>
        </Modal>

      </Layout>
    </ProtectedRoute>
  )
}

export default Courses